import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CheckCircle2, CloudUpload, Lock, Paperclip, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import BrandLogo from '../../components/BrandLogo';
import GeneratedByFooter from '../../components/public/GeneratedByFooter';
import { formatBytes } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import {
  resolveFileRequest,
  uploadToFileRequest,
  verifyFileRequestPassword,
} from './fileRequestApi';

export default function PublicFileRequestPage() {
  const { token } = useParams();
  const fileInput = useRef(null);
  const [password, setPassword] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [email, setEmail] = useState('');
  const [files, setFiles] = useState([]);
  const [done, setDone] = useState(0);
  // Per-file outcome: 'done' | error message. Keyed by name-size-lastModified.
  const [results, setResults] = useState({});
  // Upload % per file (fileKey → percent) while a batch is uploading.
  const [progress, setProgress] = useState({});

  const fileKey = (f) => `${f.name}-${f.size}-${f.lastModified}`;

  const meta = useQuery({
    queryKey: ['file-request-meta', token],
    queryFn: () => resolveFileRequest(token),
    retry: false,
  });

  // No password → unlock immediately once metadata loads.
  useEffect(() => {
    if (meta.isSuccess && meta.data && !meta.data.requiresPassword) setUnlocked(true);
  }, [meta.isSuccess, meta.data]);

  const verifyMutation = useMutation({
    mutationFn: () => verifyFileRequestPassword(token, password),
    onSuccess: () => setUnlocked(true),
    onError: (err) => toast.error(getErrorMessage(err, 'Incorrect password')),
  });

  // Uploads run sequentially and keep going past failures, so one bad file
  // (too big, wrong type) doesn't abort the rest. Retries skip completed files.
  const uploadMutation = useMutation({
    mutationFn: async () => {
      let ok = 0;
      const outcomes = { ...results };
      for (const file of files) {
        const key = fileKey(file);
        if (outcomes[key] === 'done') {
          ok += 1;
          continue;
        }
        try {
          await uploadToFileRequest(token, {
            file,
            uploaderName: name,
            uploaderEmail: email,
            password,
            onProgress: (percent) => setProgress((p) => ({ ...p, [key]: percent })),
          });
          ok += 1;
          outcomes[key] = 'done';
        } catch (err) {
          outcomes[key] = getErrorMessage(err, 'Upload failed');
        }
        setDone(ok);
        setResults({ ...outcomes });
      }
      if (ok < files.length) {
        const failed = files.length - ok;
        throw new Error(`${failed} file${failed > 1 ? 's' : ''} could not be uploaded`);
      }
      return ok;
    },
    onError: (err) => toast.error(err.message),
  });

  const addFiles = (list) => {
    // Snapshot synchronously: FileList is LIVE, and the onChange handler clears
    // input.value right after this call — by the time React runs the state
    // updater the list would already be empty and nothing would be added.
    const picked = Array.from(list);
    setFiles((prev) => [...prev, ...picked]);
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const startUpload = () => {
    if (!name.trim()) {
      setNameError('Please enter your name first');
      return;
    }
    setNameError('');
    uploadMutation.mutate();
  };

  const card = (children) => (
    <main className="flex min-h-screen flex-col bg-slate-100 px-4 pt-10">
      <div className="flex flex-1 items-start justify-center">
        <div className="w-full max-w-xl">
        <div className="mb-6 flex justify-center"><BrandLogo className="h-10 w-auto max-w-full" /></div>
        <div className="rounded-2xl bg-slate-100 p-6 shadow-lg ring-1 ring-slate-200">{children}</div>
        </div>
      </div>
      <GeneratedByFooter />
    </main>
  );

  if (meta.isLoading) return card(<Spinner />);
  if (meta.isError)
    return card(
      <p className="py-8 text-center text-sm text-red-600">
        {getErrorMessage(meta.error, 'This request is unavailable or has closed.')}
      </p>,
    );

  if (meta.data.requiresPassword && !unlocked) {
    return card(
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <Lock className="h-8 w-8 text-slate-400" />
          <h1 className="text-base font-semibold text-slate-800">Password required</h1>
          <p className="text-sm text-slate-500">Enter the password to upload to this request.</p>
        </div>
        <Input
          type="password"
          label="Password"
          value={password}
          autoFocus
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && password && verifyMutation.mutate()}
        />
        <Button
          className="w-full"
          loading={verifyMutation.isPending}
          disabled={!password}
          onClick={() => verifyMutation.mutate()}
        >
          Continue
        </Button>
      </div>,
    );
  }

  if (uploadMutation.isSuccess) {
    return card(
      <div className="flex flex-col items-center gap-3 py-4 text-center">
        <CheckCircle2 className="h-12 w-12 text-emerald-500" />
        <h1 className="text-lg font-semibold text-slate-800">Upload complete</h1>
        <p className="text-sm text-slate-500">
          {done} file{done > 1 ? 's' : ''} sent to “{meta.data.title}”. Thank you!
        </p>
        <Button
          variant="secondary"
          onClick={() => {
            setFiles([]);
            setDone(0);
            setResults({});
            uploadMutation.reset();
          }}
        >
          Upload more files
        </Button>
      </div>,
    );
  }

  const maxSizeMb = meta.data.maxFileSizeBytes
    ? Math.round(Number(meta.data.maxFileSizeBytes) / 1024 / 1024)
    : null;
  const hasFailures = files.some((f) => {
    const status = results[fileKey(f)];
    return status && status !== 'done';
  });

  return card(
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-800">{meta.data.title}</h1>
        {meta.data.instructions && (
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-500">{meta.data.instructions}</p>
        )}
        <p className="mt-2 text-xs text-slate-400">
          {meta.data.allowedTypes?.length
            ? `Accepted: ${meta.data.allowedTypes.join(', ')}. `
            : ''}
          {maxSizeMb ? `Max ${maxSizeMb} MB per file. ` : ''}
          {meta.data.maxFiles ? `Up to ${meta.data.maxFiles} files.` : ''}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Your name *"
          value={name}
          error={nameError}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError && e.target.value.trim()) setNameError('');
          }}
        />
        <Input
          label="Email (optional)"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 py-8 text-slate-500 transition-colors hover:border-brand-500 hover:text-brand-600"
      >
        <CloudUpload className="h-8 w-8" />
        <span className="text-sm font-medium">Click to choose files</span>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </button>

      {files.length > 0 && (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
          {files.map((f, i) => {
            const status = results[fileKey(f)];
            return (
              <li key={fileKey(f)} className="flex items-center gap-2 px-3 py-2 text-sm">
                {status === 'done' ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                ) : (
                  <Paperclip className="h-4 w-4 shrink-0 text-slate-400" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-slate-700">{f.name}</span>
                  {status && status !== 'done' && (
                    <span className="block truncate text-xs text-red-600">{status}</span>
                  )}
                </span>
                {uploadMutation.isPending && !status && progress[fileKey(f)] != null && (
                  <span className="text-xs font-medium text-brand-600">
                    {progress[fileKey(f)]}%
                  </span>
                )}
                <span className="text-xs text-slate-400">{formatBytes(f.size)}</span>
                {status !== 'done' && !uploadMutation.isPending && (
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="rounded p-1 text-slate-400 hover:text-red-500"
                    aria-label="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasFailures && !uploadMutation.isPending && (
        <p className="text-sm text-red-600">
          Some files could not be uploaded — remove them or retry. Successful files won&apos;t be
          sent twice.
        </p>
      )}

      <Button
        className="w-full"
        loading={uploadMutation.isPending}
        disabled={files.length === 0}
        onClick={startUpload}
      >
        {uploadMutation.isPending
          ? `Uploading ${done}/${files.length}…`
          : hasFailures
            ? 'Retry failed uploads'
            : `Upload ${files.length || ''} file${files.length === 1 ? '' : 's'}`}
      </Button>
    </div>,
  );
}
